import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Target, Users, Calendar, Tag, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { mockEvents } from '@/lib/mockData';

const audienceSegments = [
  { id: 'all', label: 'All Customers', count: 2547, icon: Users },
  { id: 'purchased', label: 'Past Purchasers', count: 1823, icon: Tag },
  { id: 'recent', label: 'Recent Activity (30 days)', count: 892, icon: Calendar },
  { id: 'vip', label: 'VIP Customers', count: 156, icon: Target }
];

export function AdminCampaignTargeting() {
  const navigate = useNavigate();
  const [selectedSegments, setSelectedSegments] = useState(['all']);
  const [selectedEvents, setSelectedEvents] = useState([]);

  const toggleSegment = (id) => {
    if (selectedSegments.includes(id)) {
      setSelectedSegments(selectedSegments.filter(s => s !== id));
    } else {
      setSelectedSegments([...selectedSegments, id]);
    }
  };

  const toggleEvent = (id) => {
    if (selectedEvents.includes(id)) {
      setSelectedEvents(selectedEvents.filter(e => e !== id));
    } else {
      setSelectedEvents([...selectedEvents, id]);
    }
  };

  const handleCreate = () => {
    navigate('/admin/campaigns/camp-001/metrics');
  };

  const totalRecipients = selectedSegments.reduce((sum, segId) => {
    const segment = audienceSegments.find(s => s.id === segId);
    return sum + (segment?.count || 0);
  }, 0);

  return (
    <AdminLayout>
      <div className="p-8 max-w-2xl mx-auto">
        <Link to="/admin/campaigns/new/info" className="inline-flex items-center text-muted-foreground hover:text-white mb-6">
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back to Campaign Info
        </Link>

        <h1 className="text-3xl font-bold text-white mb-2">Target Audience</h1>
        <p className="text-muted-foreground mb-8">Choose who will receive this campaign</p>

        {/* Step Indicator */}
        <div className="flex items-center gap-4 mb-8">
          <div className="flex items-center gap-2 text-primary">
            <div className="w-8 h-8 rounded-full bg-primary/30 text-primary flex items-center justify-center text-sm font-semibold">
              <Check className="w-4 h-4" />
            </div>
            <span className="text-sm">Campaign Info</span>
          </div>
          <div className="flex-1 h-px bg-border max-w-[60px]" />
          <div className="flex items-center gap-2 text-primary">
            <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">2</div>
            <span className="text-sm font-medium">Targeting</span>
          </div>
        </div>

        <div className="space-y-6">
          {/* Audience Segments */}
          <Card className="bg-card border-border p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Audience Segments</h2>
                <p className="text-sm text-muted-foreground">Select one or more segments</p>
              </div>
            </div>

            <div className="space-y-3">
              {audienceSegments.map(segment => (
                <div 
                  key={segment.id}
                  className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-all ${
                    selectedSegments.includes(segment.id)
                      ? 'bg-primary/20 border border-primary'
                      : 'bg-secondary/50 border border-transparent hover:border-border'
                  }`}
                  onClick={() => toggleSegment(segment.id)}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox checked={selectedSegments.includes(segment.id)} />
                    <segment.icon className="w-5 h-5 text-muted-foreground" />
                    <span className="text-white font-medium">{segment.label}</span>
                  </div>
                  <span className="text-muted-foreground">{segment.count.toLocaleString()} contacts</span>
                </div>
              ))}
            </div>
          </Card>

          {/* Event Filter */}
          <Card className="bg-card border-border p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-accent" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Filter by Event (Optional)</h2>
                <p className="text-sm text-muted-foreground">Only send to customers who attended specific events</p>
              </div>
            </div>

            <div className="space-y-3">
              {mockEvents.slice(0, 3).map(event => (
                <div 
                  key={event.id}
                  className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-all ${
                    selectedEvents.includes(event.id)
                      ? 'bg-accent/20 border border-accent'
                      : 'bg-secondary/50 border border-transparent hover:border-border'
                  }`}
                  onClick={() => toggleEvent(event.id)}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox checked={selectedEvents.includes(event.id)} />
                    <img src={event.image} alt={event.title} className="w-10 h-10 rounded-lg object-cover" />
                    <span className="text-white font-medium">{event.title}</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Summary */}
          <Card className="bg-primary/10 border-primary/30 p-4">
            <div className="flex items-center justify-between">
              <span className="text-white font-medium">Estimated Recipients</span>
              <span className="text-2xl font-bold text-primary">{totalRecipients.toLocaleString()}</span>
            </div>
          </Card>

          <div className="flex gap-3">
            <Link to="/admin/campaigns/new/info" className="flex-1">
              <Button variant="outline" className="w-full border-border">
                Back
              </Button>
            </Link>
            <Button 
              className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={handleCreate}
            >
              Create Campaign
            </Button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminCampaignTargeting;
